import { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ArrowUpRight, X, Play, Volume2, VolumeX } from 'lucide-react';

const Projects = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [isMuted, setIsMuted] = useState(true);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const filters = [
    { id: 'all', label: 'All Projects' },
    { id: 'aluminium', label: 'Aluminium' },
    { id: 'glass', label: 'Glass' },
    { id: 'maintenance', label: 'Maintenance' },
  ];

  const projects = [
    {
      id: 1,
      title: 'Modern Glass Installation',
      category: 'glass',
      description: 'Premium glass installation showcasing our precision craftsmanship and quality materials.',
      video: 'https://customer-assets.emergentagent.com/job_build-preview-73/artifacts/fioxp8kw_WhatsApp%20Video%202026-04-27%20at%209.26.26%20AM.mp4',
      isVideo: true,
      span: 'md:col-span-8',
      tall: true,
    },
    {
      id: 2,
      title: 'Aluminium Framework',
      category: 'aluminium',
      description: 'Custom aluminium framework installation for commercial property.',
      video: 'https://customer-assets.emergentagent.com/job_build-preview-73/artifacts/5pbyyk0f_WhatsApp%20Video%202026-04-27%20at%209.24.45%20AM.mp4',
      isVideo: true,
      span: 'md:col-span-4',
    },
    {
      id: 3,
      title: 'Glass Partition Work',
      category: 'glass',
      description: 'Elegant glass partitions installed with precision and care.',
      video: 'https://customer-assets.emergentagent.com/job_build-preview-73/artifacts/wnvve5ax_WhatsApp%20Video%202026-04-27%20at%209.26.24%20AM.mp4',
      isVideo: true,
      span: 'md:col-span-4',
    },
    {
      id: 4,
      title: 'High-Rise Windows',
      category: 'aluminium',
      description: 'Premium aluminium window installation for residential tower.',
      image: 'https://images.unsplash.com/photo-1687157351927-5a406bc6a3fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxyZXNpZGVudGlhbCUyMHRvd2VyJTIwZ2xhc3MlMjB3aW5kb3dzJTIwY2xvc2UlMjB1cHxlbnwwfHx8fDE3NzQ1NTY4MjN8MA&ixlib=rb-4.1.0&q=85',
      isVideo: false,
      span: 'md:col-span-4',
    },
    {
      id: 5,
      title: 'Glass Partition System',
      category: 'glass',
      description: 'Modern glass partition walls for open-plan office space.',
      image: 'https://images.pexels.com/photos/5483051/pexels-photo-5483051.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      isVideo: false,
      span: 'md:col-span-6',
    },
    {
      id: 6,
      title: 'Residential Complex',
      category: 'maintenance',
      description: 'Complete aluminium doors and windows for 50-unit residential project.',
      image: 'https://images.pexels.com/photos/2462015/pexels-photo-2462015.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      isVideo: false,
      span: 'md:col-span-6',
    },
  ];

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(p => p.category === activeFilter);

  return (
    <section 
      id="projects" 
      className="py-24 md:py-32 px-6 bg-sky-50"
      data-testid="projects-section"
      ref={ref}
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div 
          className="mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <motion.span 
            className="text-xs tracking-[0.2em] uppercase font-semibold text-sky-600 block mb-4 flex items-center gap-3"
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <span className="w-8 h-px bg-sky-500"></span>
            Our Portfolio
          </motion.span>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <motion.h2 
                className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-sky-900 mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                Featured Projects
              </motion.h2>
              <motion.p 
                className="text-sky-700 max-w-xl text-lg"
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                Explore our recent work across residential and commercial sectors in the UAE.
              </motion.p>
            </div>

            {/* Filter Tabs */}
            <motion.div 
              className="flex flex-wrap gap-3"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              {filters.map((filter) => (
                <motion.button
                  key={filter.id}
                  onClick={() => setActiveFilter(filter.id)}
                  className={`filter-tab ${activeFilter === filter.id ? 'active' : ''}`}
                  data-testid={`filter-${filter.id}`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {filter.label}
                </motion.button>
              ))}
            </motion.div>
          </div>
        </motion.div>

        {/* Projects Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-12 gap-6"
          layout
        >
          <AnimatePresence mode="popLayout">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                className={`project-card group ${project.span} overflow-hidden rounded-xl cursor-pointer`}
                data-testid={`project-card-${project.id}`}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                whileHover={{ scale: 1.02 }}
                onClick={() => setSelectedProject(project)}
              >
                <div className={`relative ${project.tall ? 'h-[500px]' : 'h-[300px]'} overflow-hidden`}>
                  {project.isVideo ? (
                    <>
                      <video
                        src={project.video}
                        className="w-full h-full object-cover"
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        onMouseEnter={(e) => e.target.play()}
                        onMouseLeave={(e) => {
                          e.target.pause();
                          e.target.currentTime = 0;
                        }}
                      />
                      {/* Play indicator */}
                      <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg z-10">
                        <Play size={18} className="text-[#25D366] ml-0.5" fill="#25D366" />
                      </div>
                    </>
                  ) : (
                    <motion.img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover"
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                    />
                  )}
                  <motion.div 
                    className="absolute inset-0 bg-gradient-to-t from-sky-900/95 via-sky-900/20 to-transparent"
                    initial={{ opacity: 0.6 }}
                    whileHover={{ opacity: 1 }}
                  />
                  <motion.div 
                    className="absolute inset-0 flex flex-col justify-end p-6"
                    initial={{ y: 20, opacity: 0 }}
                    whileHover={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <span className="text-xs tracking-[0.15em] uppercase text-[#25D366] mb-2 font-medium">
                      {project.category}
                    </span>
                    <h3 
                      className="text-xl sm:text-2xl font-normal text-white mb-2"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      {project.title}
                    </h3>
                    <p className="text-sky-200 text-sm line-clamp-2 mb-4">
                      {project.description}
                    </p>
                    <div className="flex items-center gap-2 text-white text-sm font-medium group-hover:text-[#25D366] transition-colors">
                      {project.isVideo ? 'Watch Video' : 'View Project'}
                      <motion.span
                        animate={{ x: [0, 4, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      >
                        <ArrowUpRight size={16} />
                      </motion.span>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Project Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedProject(null)}
          >
            {/* Close button */}
            <button
              className="absolute top-6 right-6 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-colors"
              onClick={() => setSelectedProject(null)}
              data-testid="modal-close-btn"
            >
              <X size={24} />
            </button>

            {/* Mute/Unmute button for videos */}
            {selectedProject.isVideo && (
              <button
                className="absolute top-6 left-6 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMuted(!isMuted);
                }}
                data-testid="video-mute-toggle"
              >
                {isMuted ? <VolumeX size={24} /> : <Volume2 size={24} />}
              </button>
            )}

            <motion.div
              className="relative max-w-3xl w-full"
              initial={{ scale: 0.9, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 50 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Media container */}
              <div className="rounded-2xl overflow-hidden shadow-2xl bg-slate-900">
                {selectedProject.isVideo ? (
                  <video
                    src={selectedProject.video}
                    className="w-full aspect-video"
                    controls
                    autoPlay
                    muted={isMuted}
                    playsInline
                  />
                ) : (
                  <img
                    src={selectedProject.image}
                    alt={selectedProject.title}
                    className="w-full aspect-video object-cover"
                  />
                )}
              </div>
              
              {/* Project info below */}
              <div className="mt-4 bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <span className="text-xs tracking-[0.15em] uppercase text-[#25D366] mb-2 block font-medium">
                  {selectedProject.category}
                </span>
                <h3 
                  className="text-xl sm:text-2xl font-medium text-white mb-3"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {selectedProject.title}
                </h3>
                <p className="text-sky-200 text-sm mb-4">
                  {selectedProject.description}
                </p>
                <motion.a
                  href="https://wa.me/971566832840?text=Hello%2C%20I%27m%20interested%20in%20a%20similar%20project"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-whatsapp inline-flex text-sm"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Inquire About This Project
                </motion.a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Projects;
